"use client";

import { Edit, Globe, Mail, MapPin, Phone, User } from "lucide-react";

import { usePageTranslation } from "@/hooks/usePageTranslation";

interface UserProfileViewProps {
  userData: any;
  onEditClick: () => void;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function UserProfileView({
  userData,
  onEditClick,
}: UserProfileViewProps) {
  const { t } = usePageTranslation();

  const notSet = t("account.notSet");
  const profileFields = [
    { label: t("account.name"), value: userData.name || notSet, icon: User },
    { label: t("account.email"), value: userData.email, icon: Mail, ltr: true },
    {
      label: t("account.mobile"),
      value: userData.mobile || notSet,
      icon: Phone,
      ltr: !!userData.mobile,
    },
    {
      label: t("checkout.fieldAddress"),
      value: userData.address || notSet,
      icon: MapPin,
    },
    { label: t("checkout.fieldCity"), value: userData.city || notSet, icon: Globe },
    {
      label: t("checkout.fieldCountry"),
      value: userData.country || notSet,
      icon: Globe,
    },
    {
      label: t("checkout.fieldPostcode"),
      value: userData.postcode || notSet,
      icon: MapPin,
    },
  ];

  return (
    <div className="user-profile-view">
      <div className="mb-6 flex items-center justify-between">
        <h2
          className="text-2xl font-bold text-brand-fg"
          style={headingFontStyle}
        >
          {t("account.profileInformation")}
        </h2>
        <button
          onClick={onEditClick}
          className="flex items-center space-x-2 rounded-brand bg-brand-primary px-4 py-2 text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
        >
          <Edit className="h-4 w-4" />
          <span>{t("account.editProfile")}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {profileFields.map((field, index) => {
          const Icon = field.icon;
          return (
            <div
              key={index}
              className="profile-field rounded-brand border border-brand-border bg-brand-fg/5 p-4"
            >
              <div className="flex items-start space-x-3">
                <div className="rounded-brand bg-brand-primary/10 p-2">
                  <Icon className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="flex-1">
                  <p className="mb-1 text-sm font-medium text-brand-muted">
                    {field.label}
                  </p>
                  <p
                    className="text-base font-semibold text-brand-fg"
                    dir={field.ltr ? "ltr" : undefined}
                  >
                    {field.value}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-brand border border-brand-primary/30 bg-brand-primary/5 p-4">
        <div className="flex items-start space-x-3">
          <div className="rounded-brand bg-brand-primary/10 p-2">
            <User className="h-5 w-5 text-brand-primary" />
          </div>
          <div>
            <p className="mb-1 text-sm font-medium text-brand-fg">
              {t("account.userId")}
            </p>
            <p className="text-sm text-brand-muted" dir="ltr">
              #{userData.userId}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
